import { useEffect, useState } from "react";
import { Highlighter, Trash2, X } from "lucide-react";

const place = (x, y) => ({
  left: Math.min(Math.max(x, 80), window.innerWidth - 80),
  top: Math.min(y + 10, window.innerHeight - 150),
});

export default function HighlightToolbar({ containerRef, onAdd, removeTarget, onRemove, onDismiss }) {
  const [sel, setSel] = useState(null);

  useEffect(() => {
    let timer;
    const read = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const s = window.getSelection();
        const text = s?.toString().trim();
        if (!s || s.isCollapsed || !text || text.length < 2 || !containerRef.current?.contains(s.anchorNode)) return setSel(null);
        const range = s.getRangeAt(0);
        const r = range.getBoundingClientRect();
        const startEl = range.startContainer.nodeType === 1 ? range.startContainer : range.startContainer.parentElement;
        const p = startEl?.closest("p[data-para]");
        let anchor = { para: null, start: null };
        if (p) {
          const pre = document.createRange();
          pre.setStart(p, 0);
          pre.setEnd(range.startContainer, range.startOffset);
          const lead = s.toString().length - s.toString().trimStart().length;
          anchor = { para: Number(p.dataset.para), start: pre.toString().length + lead };
        }
        setSel({ text: text.slice(0, 1500), x: r.left + r.width / 2, y: r.bottom, ...anchor });
      }, 180);
    };
    document.addEventListener("selectionchange", read);
    window.addEventListener("scroll", read, { passive: true });
    return () => {
      clearTimeout(timer);
      document.removeEventListener("selectionchange", read);
      window.removeEventListener("scroll", read);
    };
  }, [containerRef]);

  if (sel) {
    return (
      <div className="fixed z-40 -translate-x-1/2" style={place(sel.x, sel.y)} data-testid="highlight-toolbar">
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            onAdd(sel.text, sel.para, sel.start);
            window.getSelection()?.removeAllRanges();
            setSel(null);
          }}
          data-testid="highlight-add-button"
          className="flex items-center gap-2 bg-primary-gold text-[#0B0C10] font-mono-ui text-[11px] uppercase tracking-[0.18em] px-4 py-2.5 shadow-[0_10px_30px_rgba(0,0,0,0.6)]"
        >
          <Highlighter className="w-4 h-4" /> Highlight
        </button>
      </div>
    );
  }

  if (!removeTarget) return null;
  return (
    <div className="fixed z-40 -translate-x-1/2 flex shadow-[0_10px_30px_rgba(0,0,0,0.6)]" style={place(removeTarget.x, removeTarget.y)} data-testid="highlight-remove-toolbar">
      <button onClick={() => onRemove(removeTarget.id)} data-testid="highlight-remove-button" className="flex items-center gap-2 bg-[#1a1b20] border border-white/15 text-text-primary font-mono-ui text-[11px] uppercase tracking-[0.18em] px-4 py-2.5 hover:text-[#d6453a]">
        <Trash2 className="w-4 h-4" /> Remove highlight
      </button>
      <button onClick={onDismiss} className="bg-[#1a1b20] border border-l-0 border-white/15 px-3 text-soft hover:text-text-primary" aria-label="Close" data-testid="highlight-remove-dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
